/**
 * App — providers globais da SPA.
 *
 * Esta task (task_019) reescreveu o `App.tsx` para conter TODOS os providers
 * que devem envolver o `RouterProvider`. Ordem (de fora para dentro):
 *
 *   1. ErrorBoundary (react-error-boundary)
 *        → captura qualquer erro de renderização em qualquer ponto da árvore;
 *        → exibe `<ErrorFallback />` (§17).
 *
 *   2. QueryClientProvider (@tanstack/react-query)
 *        → server state global. Configurado com defaults conservadores:
 *          • staleTime: 30s    — evita refetch agressivo em navegação rápida
 *          • gcTime:    5min   — mantém dados em cache após desmonte
 *          • retry:     1      — uma tentativa de retry em erro de rede
 *          • refetchOnWindowFocus: false — desktop-only, sem foco multi-aba
 *
 *   3. I18nProvider
 *        → expõe `useTranslation()` em toda a árvore (§5).
 *
 *   4. Toaster (sonner)
 *        → renderiza o portal de toasts (§15). Instanciado UMA VEZ aqui.
 *        → posição top-right, com closeButton e richColors.
 *
 *   5. {children}  — onde o `main.tsx` injeta o `<RouterProvider />`.
 *
 * O `RouterProvider` em si vive no `main.tsx` para manter a configuração de
 * rotas separada dos providers de UI/state (separação de responsabilidades
 * recomendada pelo React Router v6).
 */
import type { ReactNode } from 'react'
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { ErrorFallback } from '@/components/common/ErrorFallback'
import { Toaster } from '@/components/ui/sonner'
import { I18nProvider } from '@/i18n/I18nProvider'

/**
 * Adapta o ErrorFallback (que tipa `error: Error`) à assinatura do
 * react-error-boundary (que tipa `error: unknown`).
 *
 * Em runtime, o react-error-boundary sempre repassa o que foi lançado —
 * normalmente um `Error`, mas potencialmente qualquer valor. Normalizamos
 * aqui para garantir que o ErrorFallback sempre receba um `Error`.
 */
function ErrorFallbackAdapter(props: FallbackProps): React.ReactElement {
  const err =
    props.error instanceof Error
      ? props.error
      : new Error(String(props.error ?? 'Unknown error'))
  return (
    <ErrorFallback
      error={err}
      resetErrorBoundary={props.resetErrorBoundary}
    />
  )
}

/**
 * QueryClient singleton — instanciado fora do componente para sobreviver a
 * re-renders do <App />. Reset entre testes deve ser feito criando uma
 * nova instância no setup de cada test (ver `tests/routing.test.tsx`).
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
})

export interface AppProps {
  /**
   * Conteúdo a ser renderizado dentro de todos os providers. Tipicamente o
   * `<RouterProvider router={...} />` injetado a partir do `main.tsx`.
   */
  children: ReactNode
}

/**
 * Componente raiz da SPA. Envolve `children` com todos os providers globais
 * e instala o `ErrorBoundary` no nível mais externo possível dentro do React.
 */
export default function App({ children }: AppProps): React.ReactElement {
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallbackAdapter}
      onError={(error, info) => {
        // Log em dev — em produção um serviço de telemetria entraria aqui.
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.error('[ErrorBoundary]', error, info)
        }
      }}
    >
      <QueryClientProvider client={queryClient}>
        <I18nProvider>
          {children}
          <Toaster position="top-right" richColors closeButton />
        </I18nProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
